import { useEffect, useState, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import classNames from 'classnames'

import { env } from '../../../../constants'
import { debounce } from '../../../../utils/general'

import { isEmail, requestCheckCompany, abortCheckCompany, requestRegister } from './model'
import styles from './styles.module.css'

const USER_CATEGORY = {
  PERSON: 'person',
  COMPANY: 'company',
}

const Form = () => {
  const {
    register, trigger, handleSubmit,
    watch, setValue, getValues,
    setError, clearErrors,
    formState: { errors }
  } = useForm()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // email check 请求状态
  const [emailChecking, setEmailChecking] = useState(false)
  // 提交请求状态
  const [submitting, setSubmitting] = useState(false)

  // 用户角色，企业和个人
  const [userCategory, setUserCategory] = useState(null)
  // 根据 email 检测生成的已被占用的 tenant
  const [existTenant, setExistTenant] = useState(null)

  // google reCaptcha 上下文
  const captchaRef = useRef(null)

  /**
   * Verify email or company in server.
   *
   * @param {string} email - The email to be verified.
   * @param {string} company - The company name associated with the email.
   * @return {Promise<boolean|string>} - A promise that resolves to a boolean or an error message.
   */
  const remoteEmailVerify = async (email, company) => {
    setEmailChecking(true)

    let result = true

    try {
      const res = await requestCheckCompany({ email, company })

      if (res.existTenant) {
        setExistTenant(res.existTenant)
      }

      setUserCategory(res.userCategory)

      if (typeof company !== 'string') {
        // 如果没有传递 company，表示需要更新 workspace 输入框的值
        setValue('workspace', res.readyTenant)
      }

      setEmailChecking(false)
    } catch (error) {
      // 过滤由于取消请求而产生的错误
      if (error.name !== 'CanceledError') {
        result = error.message
        setEmailChecking(false)
      }
    }

    return result
  }

  /**
   * email input 内容发生改变时的回调
   */
  const handleChangeEmail = debounce(async (e) => {
    // 当输入值发生改变时取消正在进行的远端校验，避免重复校验
    abortCheckCompany()

    // 清理用户角色
    setUserCategory(null)

    // 清理根据 email 获得的数据
    setExistTenant(null)
    setValue('workspace', null)

    // 清理错误信息
    clearErrors('remoteEmailVerify')

    if (isEmail(e.target.value)) {
      // 发起校验
      const result = await remoteEmailVerify(e.target.value)

      if (result !== true) {
        setError('remoteEmailVerify', {
          type: 'remoteEmailVerify',
          message: result
        })
      }
    }
  }, 100)

  /**
   * workspace input 内容发生改变时的回调
   */
  const handleChangeWorkspace = debounce(async (e) => {
    // 当输入值发生改变时取消正在进行的远端校验，避免重复校验
    abortCheckCompany()

    // 清理 email check 获得的被占用的 tenant
    // 可用 tenant 不用清理
    setExistTenant(null)

    // 清理错误信息
    clearErrors('remoteWorkspaceVerify')

    // 长度检查，当发现输入长度不符合要求时，可以不进行校验
    if (e.target.value.length < 5 || e.target.value.length > 32) {
      return
    }

    // 发起校验
    const result = await remoteEmailVerify(getValues('email'), e.target.value)

    if (result !== true) {
      setError('remoteWorkspaceVerify', {
        type: 'remoteWorkspaceVerify',
        message: result
      })
    }
  }, 100)

  const uploadRegisterInfo = (data) => {
    const parameters = {}
    for (let pair of searchParams.entries()) {
      parameters[pair[0]] = pair[1]
    }

    const params = {
      ...data,
      company: data.workspace,
      usecase: searchParams.get('usecase'),
      parameters: parameters,
    }

    return requestRegister(params)
      .then(data => {
        const searchParams = new URLSearchParams()
        searchParams.set('email', params.email)
        searchParams.set('token', data.reg_token)
        searchParams.set('workspace', data.company)

        navigate(`/email?${searchParams.toString()}`, { replace: true })
      })
  }

  const onSubmit = (data) => {
    if (submitting === true) {
      return
    }

    if (!data.gtoken) {
      setError('root.recaptcha', {
        type: 'token',
        message: 'Please complete the reCAPTCHA verification to continue.',
      })
      return
    }

    setSubmitting(true)
    uploadRegisterInfo(data).catch(e => {
      console.error(e)
      // 针对 recaptcha 只处理失败的场景，因为成功时直接就跳转页面了
      captchaRef.current.reset()
      setValue('gtoken', undefined)
      setError('root.submit', {
        type: 'submit',
        message: e.message,
      })
    }).finally(() => {
      setSubmitting(false)
    })
  }

  const handleGtokenChange = useCallback((token) => {
    setValue('gtoken', token)
  }, [setValue])

  useEffect(() => {
    // 订阅实时校验
    const subscription = watch((value, { name, type }) => {
      trigger(name)
    })

    return () => subscription.unsubscribe()
  }, [trigger, watch])

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={classNames(styles.fieldset, styles.full)}>
        <div className={styles.fieldset_flex}>
          <div>
            {/* <label htmlFor="edit-email">
              <span className={styles.label}>Email</span>
            </label> */}
            <input
              {...register('email', {
                required: true,
                validate: {
                  isEmail: isEmail,
                },
                onChange: handleChangeEmail,
              })}
              id="email"
              className={styles.input}
              placeholder="Email"
            />
            {errors.email?.type === 'required' && <label htmlFor="email" className={styles.field_error}>This field is required.</label>}
            {errors.email?.type === 'isEmail' && <label htmlFor="email" className={styles.field_error}>Please enter a valid email address.</label>}
            {errors.remoteEmailVerify?.type === 'remoteEmailVerify' && <label htmlFor="email" className={styles.field_error}>{errors.remoteEmailVerify.message}</label>}
          </div>
        </div>
      </div>

      <div className={styles.fieldset}>
        <div className={styles.fieldset_flex}>
          <div>
            {/* <label htmlFor="edit-first-name">
              <span className={styles.label}>First Name</span>
            </label> */}
            <input
              {...register('firstName', { required: true })}
              id="first-name"
              className={styles.input}
              placeholder="First Name"
            />
            {errors.firstName && <label htmlFor="first-name" className={styles.field_error}>This field is required.</label>}
          </div>
        </div>
      </div>

      <div className={styles.fieldset}>
        <div className={styles.fieldset_flex}>
          <div>
            {/* <label htmlFor="edit-last-name">
              <span className={styles.label}>Last Name</span>
            </label> */}
            <input
              {...register('lastName', { required: true })}
              id="last-name"
              className={styles.input}
              placeholder="Last Name"
            />
            {errors.lastName && <label htmlFor="last-name" className={styles.field_error}>This field is required.</label>}
          </div>
        </div>
      </div>

      {userCategory === USER_CATEGORY.PERSON && (
        <div className={classNames(styles.fieldset, styles.full)}>
          <div className={styles.fieldset_flex}>
            <div>
              <span className={styles.label}>
                <b>Your personal workspace URL will be:</b>
              </span>
              <span className={classNames(styles.workspace_url, 'word-break')}>
                {`https://${getValues('workspace')}.${env.RUNTIME_DOMAIN}`}
              </span>
            </div>
          </div>
        </div>
      )}

      {userCategory === USER_CATEGORY.COMPANY && (
        <div className={classNames(styles.fieldset, styles.full)} >
          <div className={styles.fieldset_flex}>
            <div>
              {existTenant
                ? (
                  <span className={styles.label} style={{ color: '#AF4C10' }}>
                    <b>The workspace name "{existTenant}" is taken, please enter your desired workspace name:</b>
                  </span>
                ) : (
                  <span className={styles.label}>
                    <b>Please enter your desired workspace name:</b>
                  </span>
                )
              }
              <p>
                <span>https://</span>
                <input
                  className={classNames(styles.input, styles.input_tenant)}
                  id="workspace"
                  {...register('workspace', {
                    required: true,
                    minLength: 5,
                    maxLength: 32,
                    onChange: handleChangeWorkspace,
                  })}
                  placeholder="Workspace name"
                />
                <span className={classNames(styles.workspace_url)}>.{env.RUNTIME_DOMAIN}</span>
              </p>
              {errors.workspace?.type === 'required' && <label htmlFor="workspace" className={styles.field_error}>This field is required.</label>}
              {(errors.workspace?.type === 'minLength' || errors.workspace?.type === 'maxLength') && <label htmlFor="workspace" className={styles.field_error}>Invalid workspace name. Must be 5-32 characters, alphanumeric only.</label>}
              {errors.remoteWorkspaceVerify?.type === 'remoteWorkspaceVerify' && <label htmlFor="workspace" className={styles.field_error}>{errors.remoteWorkspaceVerify.message}</label>}
            </div>
          </div>
        </div>
      )}

      <div {...register('gtoken')} className={styles.g_recaptcha}>
        <ReCAPTCHA
          sitekey={env.GRECAPTCHA_SITE_KEY}
          ref={captchaRef}
          onChange={handleGtokenChange}
        />
      </div>

      <div className={styles.form_action}>
        <input
          type="submit"
          className={classNames(styles.submit, {
            [styles.loading]: submitting || emailChecking,
          })}
          value="Get Started"
          disabled={submitting || emailChecking}
        />
        {errors.root?.recaptcha?.type === 'token' && <span className={styles.field_error}>{errors.root?.recaptcha?.message}</span>}
        {errors.root?.submit?.type === 'submit' && <span className={styles.field_error}>{errors.root?.submit?.message}</span>}
      </div>
    </form>
  )
}

export default Form
